import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CloudUpload as UploadIcon,
  ViewModule as GridIcon,
  ViewList as ListIcon,
  Work as WorkIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { jobsAPI } from '../services/api';

// ============================================
// SUMMARY CARDS COMPONENT
// ============================================
const SummaryCards = ({ jobs }) => {
  const totalJobs = jobs.length;
  const completedJobs = jobs.filter(j => j.status?.toUpperCase() === 'COMPLETED').length;
  const inProgressJobs = totalJobs - completedJobs;
  const companiesProcessed = jobs.reduce((sum, j) => sum + (j.successful_companies || j.processed_companies || 0), 0);
  const successRate = totalJobs > 0 ? Math.round((completedJobs / totalJobs) * 100) : 0;

  const cards = [
    {
      title: 'Total Jobs',
      value: totalJobs,
      icon: <WorkIcon />,
      color: '#1976d2',
    },
    {
      title: 'Completed',
      value: completedJobs,
      subtitle: `${successRate}% success rate`,
      icon: <CheckCircleIcon />,
      color: '#2e7d32',
    },
    {
      title: 'In Progress',
      value: inProgressJobs,
      icon: <HourglassIcon />,
      color: '#ed6c02',
    },
    {
      title: 'Companies Processed',
      value: companiesProcessed,
      icon: <BusinessIcon />,
      color: '#9c27b0',
    },
  ];

  return (
    <Grid container spacing={2} sx={{ mb: 4 }}>
      {cards.map((card, index) => (
        <Grid item xs={6} sm={3} key={index}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {card.title}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: card.color }}>
                  {card.value}
                </Typography>
                {card.subtitle && (
                  <Typography variant="caption" color="text.secondary">
                    {card.subtitle}
                  </Typography>
                )}
              </Box>
              <Box sx={{ color: card.color, opacity: 0.7 }}>
                {card.icon}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

// ============================================
// PROGRESS BAR COMPONENT
// ============================================
const ProgressDisplay = ({ job }) => {
  const isManualExtraction = job.job_type?.toUpperCase() === 'MANUAL_EXTRACTION';
  
  // For Manual Extraction jobs, use leads progress from leads_enriched table
  // For other jobs, use legacy processed_companies/total_companies fields
  const total = isManualExtraction 
    ? (job.leads_total || 0)
    : (job.total_companies || job.processed_companies || job.files_uploaded_count || 0);
  
  const processed = isManualExtraction
    ? (job.leads_processed || 0)
    : (job.processed_companies || 0);
  
  const progress = total > 0 ? Math.round((processed / total) * 100) : 0;
  const displayText = `${processed} / ${total}`;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
      <Box sx={{ flexGrow: 1 }}>
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ 
            height: 8, 
            borderRadius: 4,
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              backgroundColor: progress === 100 ? '#2e7d32' : '#1976d2',
            }
          }}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
        {displayText}
      </Typography>
    </Box>
  );
};

// ============================================
// STATUS CHIP COMPONENT
// ============================================
const StatusChip = ({ status }) => {
  const statusUpper = status?.toUpperCase();
  const getStatusProps = () => {
    switch (statusUpper) {
      case 'COMPLETED':
        return { color: 'success', label: 'Completed' };
      case 'FAILED':
        return { color: 'error', label: 'Failed' };
      case 'PROCESSING':
        return { color: 'warning', label: 'Processing' };
      case 'PENDING':
        return { color: 'warning', label: 'Pending' };
      default:
        return { color: 'default', label: status || 'Unknown' };
    }
  };

  const props = getStatusProps();
  return <Chip label={props.label} color={props.color} size="small" />;
};

// Helper to derive job status for Manual Extraction jobs based on leads progress
const getDerivedJobStatus = (job) => {
  const isManualExtraction = job.job_type?.toUpperCase() === 'MANUAL_EXTRACTION';
  
  // For Manual Extraction jobs with leads, derive status from leads progress
  if (isManualExtraction && job.leads_total > 0) {
    const leadsProcessed = job.leads_processed || 0;
    const leadsTotal = job.leads_total || 0;
    
    if (leadsProcessed >= leadsTotal) {
      return 'COMPLETED';
    } else {
      // Leads still pending - show as Processing
      return 'PROCESSING';
    }
  }
  
  // For other jobs or Manual Extraction without leads yet, use DB status
  return job.status;
};

// ============================================
// TYPE CHIP COMPONENT
// ============================================
const TypeChip = ({ jobType }) => {
  const typeUpper = jobType?.toUpperCase();
  const getTypeLabel = () => {
    switch (typeUpper) {
      case 'TEXT_FILE':
        return 'Text File';
      case 'MANUAL_EXTRACTION':
        return 'Manual Extraction';
      case 'EXCEL_FILE':
        return 'Excel File';
      case 'JSON_FILE':
        return 'JSON File';
      case 'WEB_SCRAPING':
        return 'Web Scraping';
      default:
        return jobType || 'Unknown';
    }
  };

  return (
    <Chip 
      label={getTypeLabel()} 
      size="small" 
      variant="outlined"
      sx={{ 
        backgroundColor: '#f5f5f5',
        borderColor: '#bdbdbd',
      }}
    />
  );
};

// ============================================
// TABLE VIEW COMPONENT
// ============================================
const JobsTableView = ({ jobs, onView, onStart, onDelete }) => {
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 'bold', width: 60 }}>ID</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: 140 }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: 180 }}>Progress</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Created</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: 100 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {jobs.map((job) => (
            <TableRow 
              key={job.id} 
              hover
              sx={{ '&:hover': { backgroundColor: '#fafafa' } }}
            >
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  #{job.id}
                </Typography>
              </TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {job.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ 
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}>
                    {job.description}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <StatusChip status={getDerivedJobStatus(job)} />
              </TableCell>
              <TableCell>
                <TypeChip jobType={job.job_type} />
              </TableCell>
              <TableCell>
                <ProgressDisplay job={job} />
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {new Date(job.created_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </Typography>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton 
                    onClick={() => onView(job.id)} 
                    size="small"
                    title="View Job"
                  >
                    <ViewIcon fontSize="small" />
                  </IconButton>
                  {job.status?.toUpperCase() === 'PENDING' && (
                    <IconButton 
                      onClick={() => onStart(job.id)} 
                      size="small" 
                      color="primary"
                      title="Start Job"
                    >
                      <PlayIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton 
                    onClick={() => onDelete(job.id)} 
                    size="small" 
                    color="error"
                    title="Delete Job"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ============================================
// GRID VIEW (CARD) COMPONENT
// ============================================
const JobCard = ({ job, onStart, onDelete, onView }) => {
  const isManualExtraction = job.job_type?.toUpperCase() === 'MANUAL_EXTRACTION';
  const filesCount = job.files_uploaded_count || 0;
  const successfulCount = job.successful_companies || 0;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        {/* Header with ID, Status, Type */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            #{job.id}
          </Typography>
          <StatusChip status={getDerivedJobStatus(job)} />
          <TypeChip jobType={job.job_type} />
        </Box>

        {/* Name */}
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          {job.name}
        </Typography>

        {/* Description */}
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: 2,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {job.description}
        </Typography>

        {/* Progress */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {job.job_type?.toUpperCase() === 'MANUAL_EXTRACTION'
                ? `${job.leads_processed || 0} / ${job.leads_total || 0} leads`
                : `${job.processed_companies || 0} / ${job.total_companies || job.processed_companies || job.files_uploaded_count || 0} companies`
              }
            </Typography>
          </Box>
          <ProgressDisplay job={job} />
        </Box>

        {/* Footer info */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            📅 {new Date(job.created_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isManualExtraction && filesCount > 0 && (
              <Typography variant="caption" color="text.secondary">
                📁 {filesCount} files
              </Typography>
            )}
            {successfulCount > 0 && (
              <Typography variant="caption" color="text.secondary">
                ✅ {successfulCount} successful
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>

      {/* Actions */}
      <Box sx={{ p: 1, pt: 0, display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
        <IconButton onClick={() => onView(job.id)} size="small" title="View Job">
          <ViewIcon fontSize="small" />
        </IconButton>
        {job.status?.toUpperCase() === 'PENDING' && (
          <IconButton onClick={() => onStart(job.id)} size="small" color="primary" title="Start Job">
            <PlayIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton onClick={() => onDelete(job.id)} size="small" color="error" title="Delete Job">
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Card>
  );
};

const JobsGridView = ({ jobs, onView, onStart, onDelete }) => {
  return (
    <Grid container spacing={2}>
      {jobs.map((job) => (
        <Grid item xs={12} sm={6} key={job.id}>
          <JobCard
            job={job}
            onStart={onStart}
            onDelete={onDelete}
            onView={onView}
          />
        </Grid>
      ))}
    </Grid>
  );
};

// ============================================
// DIALOGS
// ============================================
const CreateJobDialog = ({ open, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    job_type: 'text_file',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
    setFormData({ name: '', description: '', job_type: 'text_file' });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Job</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Job Name"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleChange}
            required
          />
          <TextField
            margin="dense"
            name="description"
            label="Description"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={formData.description}
            onChange={handleChange}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Job Type</InputLabel>
            <Select
              name="job_type"
              value={formData.job_type}
              label="Job Type"
              onChange={handleChange}
            >
              <MenuItem value="text_file">Text File</MenuItem>
              <MenuItem value="excel_file">Excel File</MenuItem>
              <MenuItem value="json_file">JSON File</MenuItem>
              <MenuItem value="web_scraping">Web Scraping</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">Create</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const UploadJobDialog = ({ open, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    job_type: 'text_file',
  });
  const [file, setFile] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (file) {
      onSubmit(formData, file);
      setFormData({ name: '', description: '', job_type: 'text_file' });
      setFile(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload File & Create Job</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Job Name"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleChange}
            required
          />
          <TextField
            margin="dense"
            name="description"
            label="Description"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={formData.description}
            onChange={handleChange}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Job Type</InputLabel>
            <Select
              name="job_type"
              value={formData.job_type}
              label="Job Type"
              onChange={handleChange}
            >
              <MenuItem value="text_file">Text File</MenuItem>
              <MenuItem value="excel_file">Excel File</MenuItem>
              <MenuItem value="json_file">JSON File</MenuItem>
            </Select>
          </FormControl>
          <input
            accept=".txt,.xlsx,.xls,.json"
            style={{ display: 'none' }}
            id="file-upload"
            type="file"
            onChange={handleFileChange}
          />
          <label htmlFor="file-upload">
            <Button
              variant="outlined"
              component="span"
              startIcon={<UploadIcon />}
              fullWidth
              sx={{ mt: 2 }}
            >
              {file ? file.name : 'Choose File'}
            </Button>
          </label>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!file}>
            Upload & Create
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// ============================================
// MAIN JOBS COMPONENT
// ============================================
const Jobs = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // Default to table

  // Auto-switch to grid on mobile
  const effectiveViewMode = isMobile ? 'grid' : viewMode;

  const { data: jobsData, isLoading, error } = useQuery('jobs', () => jobsAPI.getJobs());

  const createJobMutation = useMutation(jobsAPI.createJob, {
    onSuccess: () => {
      queryClient.invalidateQueries('jobs');
      setCreateDialogOpen(false);
    },
  });

  const uploadJobMutation = useMutation(
    ({ jobData, file }) => jobsAPI.uploadFile(file, jobData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('jobs');
        setUploadDialogOpen(false);
      },
    }
  );

  const startJobMutation = useMutation(jobsAPI.startJob, {
    onSuccess: () => {
      queryClient.invalidateQueries('jobs');
    },
  });

  const deleteJobMutation = useMutation(jobsAPI.deleteJob, {
    onSuccess: () => {
      queryClient.invalidateQueries('jobs');
    },
  });

  const handleCreateJob = (jobData) => {
    createJobMutation.mutate(jobData);
  };

  const handleUploadJob = (jobData, file) => {
    uploadJobMutation.mutate({ jobData, file });
  };

  const handleStartJob = (jobId) => {
    startJobMutation.mutate(jobId);
  };

  const handleDeleteJob = (jobId) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      deleteJobMutation.mutate(jobId);
    }
  };

  const handleViewJob = (jobId) => {
    navigate(`/jobs/${jobId}`);
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load jobs
      </Alert>
    );
  }

  const jobs = jobsData || [];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Jobs</Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor and manage your data processing jobs
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
          >
            Upload File
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            New Job
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <SummaryCards jobs={jobs} />

      {/* All Jobs Section Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">All Jobs</Typography>
        {!isMobile && (
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="grid" title="Grid View">
              <GridIcon />
            </ToggleButton>
            <ToggleButton value="table" title="Table View">
              <ListIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {/* Jobs List */}
      {jobs.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" align="center" color="textSecondary">
              No jobs found. Create your first job to get started.
            </Typography>
          </CardContent>
        </Card>
      ) : effectiveViewMode === 'table' ? (
        <JobsTableView
          jobs={jobs}
          onView={handleViewJob}
          onStart={handleStartJob}
          onDelete={handleDeleteJob}
        />
      ) : (
        <JobsGridView
          jobs={jobs}
          onView={handleViewJob}
          onStart={handleStartJob}
          onDelete={handleDeleteJob}
        />
      )}

      {/* Dialogs */}
      <CreateJobDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateJob}
      />

      <UploadJobDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSubmit={handleUploadJob}
      />
    </Box>
  );
};

export default Jobs;
